import { Injectable } from "@angular/core";
import { environment } from "@edp-environments/environment";
import { AppRecognition, AppRecognitionUser } from "@edp-models/app-recognition.model";
import { AndroidApp, AppPostMessage, StatusBarConfig, Webkit, Window } from "@edp-models/app.model";
import { MedalliaTracking } from "@edp-models/medallia-tracking.model";
import { EdpUser } from "@edp-models/user.model";
import { getUser } from "@edp-store/selectors/user.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class AppService {
    public onReceive = {};
    public isKampyleSDKLoad: boolean;
    protected edpUser: EdpUser;

    constructor(private store: Store<IAppState>) {
        window.NativeAppCallback = this.parseEvent.bind(this);
        this.isKampyleSDKLoad = !!window.KAMPYLE_ONSITE_SDK;

        this.store.pipe(select(getUser)).subscribe(user => { 
            if (user) {
                this.edpUser = user;
            }
        });
    }

    private call(message: AppPostMessage) {
        if (!environment.isApp) {
            return;
        }

        const currentEvent: string = Object.keys(message)[0];
        if (this.onReceive[currentEvent]) {
            this.onReceive[currentEvent].complete();
        }
        this.onReceive[currentEvent] = new Subject();
        try {
            webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(message));
            return this.onReceive[currentEvent];
        } catch (e) {}

        try {
            window.webkit.messageHandlers.interOp.postMessage(message);
            return this.onReceive[currentEvent];
        } catch (e) {}

        try {
            HybridCommunication.postMessage(JSON.stringify(message));
            return this.onReceive[currentEvent];
        } catch (e) {}

        try {
            window.parent.postMessage(JSON.stringify(message), location.origin);
            return this.onReceive[currentEvent];
        } catch (e) {}
    }

    private parseEvent(operation: string, info: string) {
        if (!this.isAValidJsonString(info)) {
            return;
        }

        let data = operation === "USER_PREFS" ? this.replacePreferencesValues(info) : JSON.parse(info);
        const event: string = this.getOperationEvent(operation);

        if (event !== operation) {
            data = {
                operation,
                info: data,
            };
        }

        this.onReceive[event].next(data);
    }

    isAValidJsonString(str: string) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    private replacePreferencesValues(data: string) {
        return JSON.parse(
            data
                .replace(/gcm.notification./g, "")
                .replace("\r", " ")
                .replace("\n", "")
        );
    }

    public splashScreen(show = false) {
        this.call({
            SPLASHSCREEN: {
                action: show ? "SHOW" : "HIDE",
            },
        } as AppPostMessage);
    }

    public statusBar() {
        const statusBarConfig: StatusBarConfig = {
            VISIBLE: "true",
            BACKGROUND: "#FFFFFF",
            TEXT_COLOR: "#333333",
        };

        this.call({
            STATUS_BAR: statusBarConfig,
        } as AppPostMessage);
    }

    public speechRecognition(speechAction: string): Subject<any> {
        return this.call({
            SPEECHRECOGNITION: {
                action: speechAction,
            },
        } as AppPostMessage);
    }

    public downloadFile(url: string) {
        this.call({
            DOWNLOAD_FILE: {
                URL: url,
            },
        } as AppPostMessage);
    }

    public openExternalLink(url: string) {
        this.call({
            EXTERNAL_LINK: {
                URL: url,
            },
        } as AppPostMessage);
    }

    public getDeviceId() {
        return this.call({
            DEVICEID: {
                Operation: "GET",
            },
        } as AppPostMessage);
    }

    public getFile() {
        return this.call({
            FILESELECT: {
                SourceType: "Camera.PictureSourceType.PHOTOLIBRARY",
            },
        } as AppPostMessage);
    }

    private getOperationEvent(operation: string) {
        switch (operation) {
            case "RECOGNITIONAVAILABLE":
            case "VOICEPERMISSION":
            case "VOICEREQUESTPERMISSION":
            case "VOICERESULT":
            case "VOICESTOP":
            case "VOICEERROR":
                return "SPEECHRECOGNITION";
            default:
                return operation;
        }
    }

    public openApp(app: string | AndroidApp) {
        return this.call({
            OPEN_APP: app,
        } as AppPostMessage);
    }

    public getPreferences(key: string) {
        return this.call({
            USER_PREFS: {
                Operation: "GET",
                Key: key,
            },
            CONTEXT_ID: "get_pref",
        } as AppPostMessage);
    }

    public deletePreferences(key: string) {
        return this.call({
            USER_PREFS: {
                Operation: "DELETE",
                Key: key,
            },
            CONTEXT_ID: "delete_pref",
        } as AppPostMessage);
    }

    public getOpenedAppPush() {
        return this.call({
            PUSH: {
                Operation: "GET",
            },
        } as AppPostMessage);
    }

    public socialShare(url: string, type?: string, message?: string) {
        return this.call({
            SHARE: {
                url,
                type,
                message,
            },
        } as AppPostMessage);
    }

    public copyToClipboard(text: string) {
        return this.call({
            CLIPBOARD: {
                action: "SET",
                text,
            },
        } as AppPostMessage);
    }

    public setMedalliaTracking(medalliaTracking: MedalliaTracking): void {
        window.medalliaTracking = medalliaTracking;

        if (window.KAMPYLE_ONSITE_SDK && typeof window.KAMPYLE_ONSITE_SDK.updatePageView === "function") {
            window.KAMPYLE_ONSITE_SDK.updatePageView();
        }
    }

    public getAppHasRecoginitionAvailable() {
        return this.call({
            RECOGNITION_AVAILABLE: {
                Operation: "GET",
            },
        } as AppPostMessage);
    }

    public changeRegion(text: string): void {
        return this.call({
            CHANGEREGION: {
                text,
            },
        } as AppPostMessage);
    }

    public setAppHasRecoginitionDefined(body: AppRecognition) {
        return this.call({
            RECOGNITION_ACTIVED: {
                Operation: "SET",
                body,
            },
        } as AppPostMessage);
    }

    public deleteRecoginitionDefined() {
        return this.call({
            RECOGNITION_ACTIVED: {
                Operation: "DELETE",
            },
        } as AppPostMessage);
    }

    public getAppHasRecoginitionActived() {
        return this.call({
            RECOGNITION_ACTIVED: {
                Operation: "GET",
            },
        } as AppPostMessage);
    }

    public setAppUser(body: AppRecognitionUser) {
        return this.call({
            SET_USER: { body },
        } as AppPostMessage);
    }
    public logout(): void {
        return this.call({
            LOGOFF: "LOGOFF",
        } as AppPostMessage);
    }

    public showKampyleFeedbackForm(formId = 29990): void {
        const medalliaTracking = new MedalliaTracking();
        
        const customParams = {
            taxNumber: this.edpUser.partnerNumber,
            userId: this.edpUser.uid
        };

        medalliaTracking.Partnernumber = this.edpUser.partnerNumber.toString();
        medalliaTracking.userId = this.edpUser.uid;

        if (window.KAMPYLE_ONSITE_SDK) {
            window["medalliaTracking"] = medalliaTracking;
            window.KAMPYLE_ONSITE_SDK.showForm(formId, { userId: this.edpUser.uid, customParams });
        }
    }

    public checkKampyleLoad(): boolean {
        return !!window.KAMPYLE_ONSITE_SDK;
    }
}

declare const webkit: Webkit;
declare const window: Window;
declare const HybridCommunication: any;
